import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from "@apollo/client/link/context";
import { onError } from '@apollo/client/link/error';
import { getAuthToken, clearSessionAndRedirectToLogin } from './utils/sessionAuth.js';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Applications from './pages/Applications';
import ApplicationDetail from './pages/ApplicationDetail';
import Profiles from './pages/Profiles';
import ProfileEditor from './pages/ProfileEditor';
import Users from './pages/Users';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import AllDevices from './pages/AllDevices';
import Locations from './pages/Locations';
import DeviceGroups from './pages/DeviceGroups';
import DdmDeclarations from './pages/DdmDeclarations';
import SoftwareUpdates from './pages/SoftwareUpdates';
import NightRest from './pages/NightRest';
import MacScripts from './pages/MacScripts';
import MacPackages from './pages/MacPackages';
import DeviceRegistrations from './pages/DeviceRegistrations';
import Classroom from './pages/Classroom';
import DepProfiles from './pages/DepProfiles';
import Enroll from './pages/Enroll';
import Settings from './pages/Settings';
import MacDetail from './pages/MacDetail';
import IPhoneDetail from './pages/IPhoneDetail';
import IPadDetail from './pages/IPadDetail';
import AppleTVDetail from './pages/AppleTVDetail';
import FilteredDeviceList from './pages/dashboardListPages/FilteredDeviceList';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ScrollToTop } from './components/ScrollToTop.jsx';

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        macs: {
          merge(existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      // RFC 6750: "Bearer " + token (kein Doppelpunkt nach Bearer)
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    }
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  const status =
    networkError?.statusCode ??
    networkError?.response?.status ??
    (networkError?.name === 'ServerError' && networkError?.response?.status);
  const unauthorizedHttp = status === 401;
  const gqlUnauthorized = graphQLErrors?.some(
    (e) =>
      e.extensions?.code === 'UNAUTHENTICATED' ||
      /not authorized/i.test(e.message || '')
  );
  if (unauthorizedHttp || gqlUnauthorized) {
    clearSessionAndRedirectToLogin();
  }
});

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_BACKEND_SERVER}/graphql`,
});

const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
});

function AppShell() {
  return (
    <>
      <Navbar/>
      <div className='page'>
        <Sidebar/>
        <div className='container'>
          <Routes>
            <Route path="/login" element={<Login />}/>
            <Route path="/" element={<Dashboard />} />
            <Route path="/all" element={<AllDevices />} />
            <Route path="/applications" element={<Applications />} />
            <Route path="/applications/:adamId" element={<ApplicationDetail />} />
            <Route path="/profiles" element={<Profiles />} />
            <Route path="/profiles/editor" element={<ProfileEditor />} />
            <Route path="/profiles/editor/:id" element={<ProfileEditor />} />
            <Route path="/users" element={<Users />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/device-groups" element={<DeviceGroups />} />
            <Route path="/classroom" element={<Classroom />} />
            <Route path="/software-updates" element={<SoftwareUpdates />} />
            <Route path="/night-rest" element={<NightRest />} />
            <Route path="/mac-scripts" element={<MacScripts />} />
            <Route path="/mac-packages" element={<MacPackages />} />
            <Route path="/ddm" element={<DdmDeclarations />} />
            <Route path="/device-registrations" element={<DeviceRegistrations />} />
            <Route path="/dep-profiles" element={<DepProfiles />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/mac/:deviceId" element={<MacDetail />} />
            <Route path="/iphone/:deviceId" element={<IPhoneDetail />} />
            <Route path="/ipad/:deviceId" element={<IPadDetail />} />
            <Route path="/appletv/:deviceId" element={<AppleTVDetail />} />
            <Route path="/appletv/by_serial/:serialNumber" element={<AppleTVDetail />} />
            <Route path="/filter/:filterType/:value" element={<FilteredDeviceList />} />
            <Route path="/filter/profile/:profile/:value/:deviceType" element={<FilteredDeviceList />} />
            <Route path="/filter/osversion/:deviceType/:OSVersion" element={<FilteredDeviceList />} />
            <Route path="/filter/appversion/:deviceType/:Name/:Version" element={<FilteredDeviceList />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <>
      <ApolloProvider client={client}>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/enroll" element={<Enroll />} />
            <Route path="/teacher" element={<Classroom fullscreen />} />
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<AppShell />} />
          </Routes>
        </Router>
        <ToastContainer position="bottom-right" theme="colored"/>
      </ApolloProvider>
    </>
  );
}

export default App;
